import { ref, computed } from 'vue';
import { useRuntimeConfig } from 'nuxt/app';
import { useFetchWithJwt } from '~/composables/useFetchWithJwt';
import { useHandleCode } from "~/composables/useHandleCode";
import { usePersistentState } from "~/composables/usePersistentState";
import {
    ApiResponse,
    ApiResponseWithPage
} from '~/types/response'
import {
    ConstantData,
} from '~/types/constant'

export function useConstantData() {
    const config = useRuntimeConfig();
    const { fetchWithJwt } = useFetchWithJwt();
    const { handleErrorCode } = useHandleCode();
    const { lang } = useLanguage();

    // 상태 관리
    const constantArrayList = usePersistentState<ConstantData[]>('constantArrayList', () => []);
    const isLoading = ref(false)
    const isInitialized = ref(false)

    const page = ref()
    const size = ref()

    /**
     * 이메일 도메인 리스트 (자주 사용되므로 별도 computed)
     */
    const emailDomainList = computed(() =>
        constantArrayList.value
            .filter(item => item.parentConstantName === 'MAIL')
            .map(item => item.constantNote)
            .filter(Boolean) // null/undefined 제거
    )

    /**
     * 기타 자주 사용되는 상수 리스트들
     */
    const getConstantOptions = (constantName: string) => {
        return computed(() =>
            constantArrayList.value
                .filter(item => item.parentConstantName === constantName)
                .map(item => ({
                    value: item.constantValue,
                    text: item.constantNote,
                    code: item.constantName
                }))
        )
    }

    const getItems = (constantName: string): ConstantData[] => {
        return constantArrayList.value.filter(item => item.parentConstantName === constantName);
    }

    const fetchConstantList = async (params: Record<string, any>): Promise<ApiResponseWithPage<ConstantData>> => {
        return (await fetchWithJwt('/constant/search', {
            method: 'POST',
            body: JSON.stringify({
                page: page.value,
                size: size.value,
                ...params,
            }),
        })) as ApiResponseWithPage<ConstantData>
    };

    const handleConstantList = async (params: Record<string, any>): Promise<void> => {
        try {
            if(!params) params = {};
            params.size = 1000;
            const response = await fetchConstantList(params);
            console.log(response);

            if (response?.result === 'success') {
                constantArrayList.value = response.data?.content.map((item) => {
                    return {
                        ...item,
                    }
                })

                //console.log('aaa',getItems('MAIL'));
            } else {
                handleErrorCode(response.message);
            }

        } catch (error) {
            console.error('Error fetching beta data:', error);
            handleErrorCode('E50001');
        }
    }

    const fetchEmailDomainList = async (): Promise<ApiResponse<ConstantData[]>> => {
        const response = await fetch(`${config.public.apiBaseUrl}/constant/email/list`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if(!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'E50001');
        }

        const data = await response.json();
        return data as ApiResponse<ConstantData[]>;
    }

    /**
     * 이메일 도메인 리스트를 가져오는 함수
     * @returns {Promise<string[]>} 이메일 도메인 리스트
     */
    const handleEmailDomainList = async (): Promise<void> => {
        try {
            const response = await fetchEmailDomainList();

            if (response?.result !== 'success') {
                handleErrorCode(response.message || 'E50001');
                return;
            }

            // API에서 받은 도메인 리스트
            const domainList = response.data as ConstantData[];

            // '직접 입력' 옵션을 맨 앞에 추가
            const customOption: ConstantData = {
                id: null, // ID는 null로 설정
                constantName: 'CUSTOM',
                parentConstantName: 'MAIL',
                constantValue: '',
                constantNote: lang.option('directInput'), // 언어팩에서 가져옴
                constantLevel: '1',
                constantOrder: null,
                isActive: 1,
                canEdit: 1,
                searchKeyword: null
            }

            constantArrayList.value = [customOption, ...domainList];
        } catch (error) {
            console.error('Error fetching email domain list:', error);
            handleErrorCode('E50001');
        }
    }

    /**
     * 초기화 함수 (앱 시작 시 한 번만 호출)
     */
    const initializeEmailConstants = async () => {
        if (!isInitialized.value && !isLoading.value) {
            await handleEmailDomainList()
        }
    }

    /**
     * 특정 상수 그룹 강제 새로고침
     */
    const refreshConstants = async (params: Record<string, any> = {}) => {
        isInitialized.value = false
        await handleConstantList(params)
    }

    return {
        getItems,
        fetchConstantList,
        handleConstantList,
        handleEmailDomainList,
        constantArrayList,
        emailDomainList,
        getConstantOptions,
        initializeEmailConstants,
        refreshConstants
    }

}